import React, { useEffect } from 'react';
import { App, Button, Col, Divider, Form, Grid, Input, InputNumber, Row, Space } from 'antd';
import { AxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { InputComReactIMask } from '../../../Components/InputComReactIMask';
import { AreaRestritaUsuarioService, PayloadDeCadastroDeResponsavel } from '../../../Services/Domain/AreaRestrita/UsuarioService';
import { Formatacoes } from '../../../Utils/Formatacao';
import { notificarErroNaRequisicao, PropriedadesDeNotificacao } from '../../../Utils/Notificacoes';
import { RegrasComunsDeValidacaoDaAR } from '../../Utils/RegrasComunsDeValidacaoDaAR';
import { TypeEndereco } from '../../../Models/Pessoa/Endereco';
import { EnderecoFormBasic } from '../../../Components/Populados/Pessoa/Endereco/EnderecoFormBasic';
import { TypeTelefone } from '../../../Models/Pessoa/Contato/Telefone';
import { InputTelefoneECelular } from '../../../Components/InputTelefoneFixoECelular';

interface IFormularioDeCadastroDeResponsavel {
    cpf: string,
    nome: string,
    sobrenome: string,
    email: string,
    telefone: TypeTelefone;
    senha: string,
    confirmacaoDeSenha: string,
    senhaDaViagem: string;
    endereco : TypeEndereco;
}

export const PainelPublicoDeCadastroDeResponsavel : React.FC<{ 
    hrefCancelar?: string,
    depoisDeCadastrar?: (a: Awaited<ReturnType<typeof AreaRestritaUsuarioService.prototype.cadastrarResponsavel>>, formulario: IFormularioDeCadastroDeResponsavel) => any
}> = (props) => {
    const { notification } = App.useApp();
    const breakpoints = Grid.useBreakpoint();

    const [ form ] = Form.useForm<IFormularioDeCadastroDeResponsavel>();
    const valoresDoFormulario = Form.useWatch([], form);

    const mutationCadastro = useMutation({
        mutationFn: (payload: PayloadDeCadastroDeResponsavel) => {
            return new AreaRestritaUsuarioService().cadastrarResponsavel(payload);
        },
        onSuccess: (data) => {
            notification.open( {
                ...PropriedadesDeNotificacao.Sucesso("Cadastro realizado com sucesso")
            });

            props.depoisDeCadastrar?.(data, valoresDoFormulario)
        },
        onError: (error: AxiosError, valores) => {
            if (error.response?.status === 401) {
                notification.error({ message: "Usuario ou senha inválidos.", placement: "top" });
            }
            else if (error.response?.status === 428 && (error.response.data as any).alterarSenha) {
                notification.info({ message: "É necessário definir sua primeira senha", placement: "top" });
            }
            else {
                notificarErroNaRequisicao(error)
            }
        }
    });

    const aoFinalizar = (valores: IFormularioDeCadastroDeResponsavel) => {

        const payload: PayloadDeCadastroDeResponsavel = {
            credenciais: {
                email: valores.email,
                senha: valores.senha
            },
            responsavel: {
                pessoa : {
                    nome: `${valores.nome.trim()} ${valores.sobrenome.trim()}`,
                    cpf: Formatacoes.somenteDigitos(valores.cpf),
                    telefones : [
                        valores.telefone
                    ],
                    enderecos : [
                        {
                            ...valores.endereco,
                            cep : Formatacoes.sanitizar("_", valores.endereco.cep) ?? ""
                        }
                    ]
                }
            }
        }

        mutationCadastro.mutate(payload);
    }

    return (
        <Form<IFormularioDeCadastroDeResponsavel>
            form={form}
            onFinish={aoFinalizar}
            autoComplete="off"
            layout="vertical"
            style={{ width: breakpoints.xs ? '85vw' : '380px', flexGrow: 1, flexWrap : 'wrap' }}
        >
            <Divider orientation="left">Dados básicos</Divider>
            <Form.Item<IFormularioDeCadastroDeResponsavel>
                label="Nome"
                name="nome"
                rules={[{ required: true }]}
            >
                <Input/>
            </Form.Item>
            <Form.Item<IFormularioDeCadastroDeResponsavel>
                label="Sobrenome"
                name="sobrenome"
                rules={[{ required: true }]}
            >
                <Input/>
            </Form.Item>
            <Form.Item<IFormularioDeCadastroDeResponsavel>
                label="CPF"
                name="cpf"
                rules={[
                    { required: true },
                    { pattern: /^\d{3}\.?\d{3}\.?\d{3}\-?\d{2}$/, message: "Informe um CPF válido." }
                ]}
            >
                <InputComReactIMask 
                    maskOptions={{
                        mask: "000.000.000-00"
                    }}
                />
            </Form.Item>

            <Form.Item<IFormularioDeCadastroDeResponsavel>
                label="E-mail"
                name="email"
                rules={[
                    { required: true },
                    { type: "email" }
                ]}
            >
                <Input />
            </Form.Item>

            <Divider orientation="left">Telefone ou celular</Divider>
            <Space.Compact style={{ width : "100%", maxWidth : "100%" }}>
                <Form.Item<IFormularioDeCadastroDeResponsavel>
                    label={"DDD"}
                    name={["telefone", "ddd"]}
                    dependencies={["telefone", "numero"]}
                    style={{ width: "30%" }}
                    rules={[{ 
                        required: true,
                        pattern: /^\d{2}$/, 
                    }]}
                >
                    <InputNumber style={{ width : "100%" }} controls={false} min={11} max={99} placeholder="DDD" />
                </Form.Item>
            
                <Form.Item<IFormularioDeCadastroDeResponsavel> 
                    label={"Número"}
                    name={["telefone", "numero"]}
                    dependencies={["telefone", "ddd"]}
                    style={{ width: "70%" }}
                    rules={[
                        {
                            required: true,
                        },
                        {
                            pattern: /^[0-9]{4,5}-?[0-9]{4}$/,
                            message: "Número não se encaixa no padrão XXXXX-XXXX ou XXXX-XXXX.",
                        }
                    ]}
                >
                    <InputTelefoneECelular />
                </Form.Item>
            </Space.Compact>
            
            <Divider orientation="left">Endereço</Divider>
            <Form.Item<IFormularioDeCadastroDeResponsavel> name={"endereco"}>
                <EnderecoFormBasic />
            </Form.Item>

            <Divider orientation="left">Informações de acesso</Divider>
            <Form.Item<IFormularioDeCadastroDeResponsavel>
                label="Senha"
                name="senha"
                rules={RegrasComunsDeValidacaoDaAR.Senha}
            >
                <Input.Password />
            </Form.Item>
            <Form.Item<IFormularioDeCadastroDeResponsavel>
                label="Confirmar Senha"
                name="confirmacaoDeSenha"
                dependencies={["senha"]}
                rules={[
                    { required: true, message: "Informe sua senha." },
                    { validator: async (rule, value) => {
                        if(value != valoresDoFormulario.senha){
                            return Promise.reject("As senhas não conferem")
                        }

                        return Promise.resolve()
                    }}
                ]}
            >
                <Input.Password visibilityToggle={false}/>
            </Form.Item>
            <Form.Item>
                <Row gutter={16} justify={'center'} style={{marginTop: "1em"}}>
                    {
                        props.hrefCancelar &&
                        <Col xs={12}>
                            <Link to={props.hrefCancelar}>
                                <Button
                                    type="primary"
                                    danger
                                    htmlType="button"
                                    disabled={mutationCadastro.isPending}
                                    block 
                                >
                                    Cancelar
                                </Button>
                            </Link>
                        </Col>
                    }
                    <Col xs={12}>
                        <Button type="primary" htmlType="submit" loading={mutationCadastro.isPending} block>Cadastrar</Button>
                    </Col>
                </Row>
            </Form.Item>
        </Form>
    );
}